import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import ActivityEvents from "const/ActivityEvents";
import DateTimeFormats from "const/DateTimeFormats";
import IntegrationServices from "const/IntegrationServices";
import React, { Fragment, useCallback, useMemo } from "react";
import RpaPlatforms from "const/RpaPlatforms";
import RpaWorkflows from "const/RpaWorkflows";
import TaskTypes from "const/TaskTypes";
import Texts from "utils/Texts";
import UserRoles from "const/UserRoles";
import moment from "moment";

const {
  TYPES: {
    BUSINESS_SETTINGS_UPDATED,
    DOCUMENT_RECOGNITION_FAILED,
    DOCUMENTS_APPROVED,
    DOCUMENTS_AUTO_APPROVED,
    DOCUMENTS_DELETED,
    DOCUMENTS_UPLOADED,
    GPT_AI_FINE_TUNE_FAILED,
    GPT_AI_FINE_TUNE_FINISHED,
    GPT_AI_FINE_TUNE_STARTED,
    NEW_BUSINESS_ADDED,
    RPA_FAILED,
    RPA_FINISHED,
    RPA_STARTED,
    TASK_ADDED,
    TASK_REMOVED,
    TASK_STATUS_CHANGED,
    TASK_TARGET_USER_CHANGED,
    TRANSACTIONS_APPROVED,
    TRANSACTIONS_ASK_CLIENT_FOLLOW_UP,
    TRANSACTIONS_ASK_CLIENT,
    TRANSACTIONS_AUTO_APPROVED,
    TRANSACTIONS_AUTO_ASK_CLIENT,
    TRANSACTIONS_AUTO_CATEGORIZED,
    TRANSACTIONS_AUTO_PAIRED,
    TRANSACTIONS_AUTO_PAIRED_AND_APPROVED,
    TRANSACTIONS_CATEGORIZED,
    TRANSACTIONS_EDITED,
    TRANSACTIONS_NEED_DOCUMENT,
    TRANSACTIONS_NOT_CATEGORIZED,
    TRANSACTIONS_REPLIED_BY_CLIENT,
    USER_INVITE_FOLLOW_UP,
    USER_INVITED,
    USER_REVOKED
  }
} = ActivityEvents;

const ODD = 2;

const getRpaFinishedDescription = ({ info, workflowNames, messages, uiTexts }) => {
  if (info.workflow === RpaWorkflows.PARSE_TRANSACTIONS
    && [RpaPlatforms.XERO, RpaPlatforms.QBO].includes(info.platform)) {
    return Texts.replaceVars(
      info.transactionsCount
        ? messages.systemEventRpaFinishedParsing
        : messages.systemEventRpaFinishedParsingZero,
      {
        count: info.transactionsCount,
        platform: info.platform === RpaPlatforms.XERO
          ? IntegrationServices.XERO.shortLabel
          : IntegrationServices.QUICK_BOOKS.shortLabel
      }
    );
  }
  if (info.workflow === RpaWorkflows.MATCH_TRANSACTIONS) {
    if (info.transactionsCount) {
      if (info.transactionsCount === info.transfersCount) {
        return Texts.replaceVars(
          messages.systemEventRpaFinishedQboMatchingTransfers,
          { count: info.transactionsCount }
        );
      } else if (info.transfersCount) {
        return Texts.replaceVars(
          messages.systemEventRpaFinishedQboMatchingWithTransfers,
          { transactionsCount: info.transactionsCount - info.transfersCount, transfersCount: info.transfersCount }
        );
      }

      return Texts.replaceVars(
        messages.systemEventRpaFinishedQboMatching,
        { count: info.transactionsCount }
      );
    }

    return messages.systemEventRpaFinishedQboMatchingZero;
  }
  if (info.workflow === RpaWorkflows.SYNC_BANKFEED) {
    return info.transactionsCount
      ? Texts.replaceVars(
        messages.systemEventRpaFinishedXeroBankFeed,
        { count: info.transactionsCount }
      )
      : messages.systemEventRpaFinishedXeroBankFeedZero;
  }
  if (info.workflow === RpaWorkflows.RECONCILE_TRANSACTONS) {
    return info.transactionsCount
      ? Texts.replaceVars(
        messages.systemEventRpaFinishedXeroReconciliation,
        { count: info.transactionsCount }
      )
      : messages.systemEventRpaFinishedXeroReconciliationZero;
  }

  return Texts.replaceVars(messages.systemEventRpaFinished, {
    workflowName: workflowNames[info.workflow] || info.workflow || uiTexts.unknown
  });
};

const useActivityEventDescription = ({ type, info = {} }) => {
  const { uiTexts, messages, errors } = useSelector(getTextsData);

  const { extraData: { integrationService } = {} } = useSelector(getSelectedBusinessData);

  const getUserName = useCallback((user, fallback = uiTexts.unknown) => {
    return (user && (user.userFullName || user.email)) || fallback;
  }, [uiTexts.unknown]);

  const monthYearStartDateText = useMemo(() => {
    return info.startDate ? moment(info.startDate).format(DateTimeFormats.MONTH_AND_YEAR_TEXT) : "";
  }, [info.startDate]);

  const workflowNames = useMemo(() => {
    return {
      [RpaWorkflows.PARSE_TRANSACTIONS]: uiTexts.parseTransactions,
      [RpaWorkflows.MATCH_TRANSACTIONS]: uiTexts.matchTransactions,
      [RpaWorkflows.SYNC_BANKFEED]: uiTexts.syncBankFeed,
      [RpaWorkflows.RECONCILE_TRANSACTONS]: uiTexts.reconcileTransactions,
      [RpaWorkflows.REMOVE_AND_REDO_TRANSACTIONS]: uiTexts.removeAndRedoTransactions
    };
  }, [uiTexts]);

  const description = useMemo(() => {
    switch (type) {
      case BUSINESS_SETTINGS_UPDATED:
        return Texts.replaceVars(messages.systemEventBusinessSettingsUpdated, {
          userName: getUserName(info.user)
        });
      case NEW_BUSINESS_ADDED:
        return Texts.replaceVars(messages.systemEventNewBusinessAdded, {
          businessName: info.businessName
        });
      case RPA_STARTED:
        return Texts.replaceVars(messages.systemEventRpaStarted, {
          workflowName: workflowNames[info.workflow] || info.workflow
        });
      case RPA_FINISHED:
        return getRpaFinishedDescription({ info, workflowNames, messages, uiTexts });
      case RPA_FAILED:
        if (info.partialResult) {
          return getRpaFinishedDescription({ info, workflowNames, messages, uiTexts });
        }

        return Texts.replaceVars(messages.systemEventRpaFailed, {
          message: info.errorMessage || errors.unknown
        });
      case GPT_AI_FINE_TUNE_STARTED:
        return messages.systemEventGptAiFineTuneStarted;
      case GPT_AI_FINE_TUNE_FINISHED:
        return info.dryRun ? messages.systemEventGptAiFineTuneFinishedDryRun : messages.systemEventGptAiFineTuneFinished;
      case GPT_AI_FINE_TUNE_FAILED:
        return Texts.replaceVars(messages.systemEventGptAiFineTuneFailed, {
          message: info.errorMessage || errors.unknown
        });
      case TRANSACTIONS_NOT_CATEGORIZED:
        return Texts.replaceVars(messages.systemEventTransactionsNotCategorized, {
          count: info.transactionsCount
        });
      case TRANSACTIONS_NEED_DOCUMENT:
        return Texts.replaceVars(messages.systemEventTransactionsNeedDocument, {
          count: info.transactionsCount
        });
      case TRANSACTIONS_AUTO_CATEGORIZED:
        return Texts.replaceVars(messages.systemEventTransactionsAutoCategorized, {
          count: info.transactionsCount
        });
      case TRANSACTIONS_AUTO_PAIRED:
        return Texts.replaceVars(messages.systemEventTransactionsAutoPaired, {
          count: info.transactionsCount
        });
      case TRANSACTIONS_AUTO_PAIRED_AND_APPROVED:
        return Texts.replaceVars(messages.systemEventTransactionsAutoPairedAndApproved, {
          count: info.transactionsCount
        });
      case TRANSACTIONS_CATEGORIZED:
        return Texts.replaceVars(messages.systemEventTransactionsCategorized, {
          userName: getUserName(info.user),
          count: info.transactionsCount
        });
      case TRANSACTIONS_AUTO_APPROVED:
        return info.queuedTransactionsCount
          ? Texts.replaceVars(messages.systemEventTransactionsAutoApprovedXeroLimit, {
            count: info.transactionsCount,
            queuedCount: info.queuedTransactionsCount
          })
          : Texts.replaceVars(messages.systemEventTransactionsAutoApproved, {
            count: info.transactionsCount
          });
      case TRANSACTIONS_APPROVED:
        return Texts.replaceVars(messages.systemEventTransactionsApproved, {
          userName: getUserName(info.user),
          count: info.transactionsCount
        });
      case TRANSACTIONS_EDITED:
        return Texts.replaceVars(messages.systemEventTransactionsEdited, {
          userName: getUserName(info.user),
          count: info.transactionsCount
        });
      case TRANSACTIONS_AUTO_ASK_CLIENT: {
        return Texts.replaceVars(messages.systemEventTransactionsAutoAskClient, {
          recipientName: getUserName(info.recipient),
          count: info.transactionsCount
        });
      }
      case TRANSACTIONS_ASK_CLIENT: {
        return Texts.replaceVars(messages.systemEventTransactionsAskClient, {
          senderName: getUserName(info.sender),
          recipientName: getUserName(info.recipient),
          count: info.transactionsCount
        });
      }
      case TRANSACTIONS_REPLIED_BY_CLIENT: {
        return Texts.replaceVars(messages.systemEventTransactionsRepliedByClient, {
          userName: getUserName(info.user),
          count: info.transactionsCount
        });
      }
      case USER_INVITED: {
        return Texts.replaceVars(messages.systemEventUserInvited, {
          senderName: getUserName(info.sender),
          recipientName: getUserName(info.recipient),
          recipientRole: (uiTexts[UserRoles.getLangId(info.recipient?.role)] || "").toLowerCase()
        });
      }
      case USER_INVITE_FOLLOW_UP: {
        return Texts.replaceVars(messages.systemEventUserInviteFollowUp, {
          recipientName: getUserName(info.recipient)
        });
      }
      case USER_REVOKED: {
        return Texts.replaceVars(messages.systemEventUserRevoked, {
          senderName: getUserName(info.sender),
          recipientName: getUserName(info.recipient),
          recipientRole: (uiTexts[UserRoles.getLangId(info.recipient?.role)] || "").toLowerCase()
        });
      }
      case TRANSACTIONS_ASK_CLIENT_FOLLOW_UP: {
        return Texts.replaceVars(messages.systemEventTransactionsAskClientFollowUp, {
          recipientName: getUserName(info.recipient),
          count: info.transactionsCount
        });
      }
      case DOCUMENTS_UPLOADED: {
        return Texts.replaceVars(messages.systemEventDocumentsUploaded, {
          userName: getUserName(info.user),
          count: info.documentsCount
        });
      }
      case DOCUMENTS_APPROVED: {
        return Texts.replaceVars(messages.systemEventDocumentsApproved, {
          userName: getUserName(info.user),
          count: info.documentsCount
        });
      }
      case DOCUMENTS_AUTO_APPROVED: {
        return Texts.replaceVars(messages.systemEventDocumentsAutoApproved, {
          userName: getUserName(info.user),
          count: info.documentsCount
        });
      }
      case DOCUMENTS_DELETED: {
        return Texts.replaceVars(messages.systemEventDocumentsDeleted, {
          userName: getUserName(info.user),
          count: info.documentsCount
        });
      }
      case DOCUMENT_RECOGNITION_FAILED: {
        return Texts.replaceVars(messages.systemEventDocumentRecognitionFailed, {
          count: info.documentsCount,
          message: info.errorMessage
        });
      }
      case TASK_ADDED: {
        return Texts.replaceVars(messages.systemEventTaskAdded, {
          senderName: getUserName(info.sender),
          recipientName: getUserName(info.recipient),
          title: info.taskTitle
        });
      }
      case TASK_REMOVED: {
        return Texts.replaceVars(messages.systemEventTaskDeleted, {
          userName: getUserName(info.user),
          title: info.taskTitle
        });
      }
      case TASK_STATUS_CHANGED: {
        const { shortLabel: serviceName } = IntegrationServices.getByValue(integrationService) || {};

        return Texts.replaceVars(messages.systemEventTaskStatusChanged, {
          userName: getUserName(info.user),
          title: info.taskTitle || ({
            [TaskTypes.TRANSACTIONS]: Texts.replaceVars(
              uiTexts.categorizeTransactionsForDate,
              { date: monthYearStartDateText }
            ),
            [TaskTypes.DOCUMENTS]: Texts.replaceVars(
              uiTexts.reviewUploadedDocumentsForDate,
              { date: monthYearStartDateText }
            ),
            [TaskTypes.AUDIT]: Texts.replaceVars(
              uiTexts.resolveCleanUpIssuesForDate,
              { date: monthYearStartDateText }
            ),
            [TaskTypes.BANK_FEEDS]: Texts.replaceVars(uiTexts.renewServiceBankConnections, { serviceName })
          })[info.taskType || info.type] || "",
          status: uiTexts[info.status]
        });
      }
      case TASK_TARGET_USER_CHANGED: {
        const { shortLabel: serviceName } = IntegrationServices.getByValue(integrationService) || {};

        return Texts.replaceVars(messages.systemEventTaskTargetUserChanged, {
          userName: getUserName(info.user),
          title: info.taskTitle || ({
            [TaskTypes.TRANSACTIONS]: Texts.replaceVars(
              uiTexts.categorizeTransactionsForDate,
              { date: monthYearStartDateText }
            ),
            [TaskTypes.DOCUMENTS]: Texts.replaceVars(
              uiTexts.reviewUploadedDocumentsForDate,
              { date: monthYearStartDateText }
            ),
            [TaskTypes.AUDIT]: Texts.replaceVars(
              uiTexts.resolveCleanUpIssuesForDate,
              { date: monthYearStartDateText }
            ),
            [TaskTypes.BANK_FEEDS]: Texts.replaceVars(uiTexts.renewServiceBankConnections, { serviceName })
          })[info.taskType] || "",
          targetUser: info.targetUser,
          status: uiTexts[info.status]
        });
      }
      // TOT: fallback
      case "taskMarkedAsCompleted": {
        return Texts.replaceVars(messages.systemEventTaskMarkedAsCompleted, {
          userName: getUserName(info.user),
          title: info.taskTitle
        });
      }
      default:
        return messages.systemEventUnknown;
    }
  }, [
    type,
    info,
    uiTexts,
    messages,
    monthYearStartDateText,
    workflowNames,
    errors.unknown,
    integrationService,
    getUserName
  ]);

  return useMemo(() => {
    return description.split("*").map((str, index) => {
      return <Fragment key={String(index + 1)}>{index % ODD ? <b>{str}</b> : <span>{str}</span>}</Fragment>;
    });
  }, [description]);
};

export default useActivityEventDescription;
